// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import OverflowMenu from "@insite/mobius/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const accountSettings = getSettingsCollection(state).accountSettings;

    return {
        checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
        cartPage: getPageLinkByPageType(state, "CartPage"),
        isPickUp: cart?.fulfillmentMethod === FulfillmentMethod.PickUp,
        cart,
        StoreStep: state.xCheckout.checkoutStep,
        enableWarehousePickup: accountSettings.enableWarehousePickup,
        cartId,
    };
};

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CheckoutStepperStyles {
    wrapperStyle?: InjectableCss;
    stepsWrapperStyles?: InjectableCss;
    stepStyles?: InjectableCss;
    stepClickableStyles?: ClickablePresentationProps;
    stepNumberStyles?: InjectableCss;
    stepTextStyles?: TypographyProps;
}

export const styles: CheckoutStepperStyles = {
    stepTextStyles: {
        lineHeight: "20px",
        weight: fontConfig.weight.semiBold,
    },
    stepNumberStyles: {
        css: css`
            color: ${getColor("text.main")};
            width: 32px;
            height: 32px;
            min-width: 32px;
            min-height: 32px;
            background-color: ${getColor("common.white")};
            border: 1px solid ${getColor("text.main")};
            text-align: center;
            line-height: 20px;
            font-weight: ${fontConfig.weight.semiBold};
            display: flex;
            justify-content: center;
            align-items: center;
            margin-right: 6px;
            border-radius: 2px;
        `,
    },
    stepsWrapperStyles: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
        `,
    },
    stepStyles: {
        css: css`
            padding-left: 5px;
            margin-bottom: 8px;
            flex: 1;
            border-top: 2px solid ${getColor("text.main")};
            padding-top: 20px;

            &:first-child {
                padding-left: 0;
            }

            &.disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }

            &.active,
            &.completed {
                border-top-color: ${getColor("primary")};
            }
        `,
    },
    stepClickableStyles: {
        css: css`
            display: flex;
            align-items: center;

            .icon-check {
                display: none;
            }

            &.active {
                .step-number,
                .step-text * {
                    color: ${getColor("primary")};
                    border-color: ${getColor("primary")};
                }
            }

            &.completed {
                .number {
                    display: none;
                }
                .step-number {
                    background-color: ${getColor("primary")};
                    border-color: ${getColor("primary")};
                }
                .icon-check {
                    display: block;
                }
                .step-text * {
                    color: ${getColor("primary")};
                }
            }

            &.disabled {
            }

            ${({ asLink }: any) =>
                asLink &&
                css`
                    padding-left: 0 !important;
                    padding-right: 5px !important;
                `}
        `,
    },
};

const CheckoutStepper: React.FC<Props> = ({
    history,
    isPickUp,
    cartPage,
    checkoutShippingPage,
    cart,
    StoreStep,
    enableWarehousePickup,
    cartId,
}) => {
    const dispatch = useAppDispatch();

    const requiresApproval = cart?.requiresApproval;
    const buildSteps = () => {
        const steps = ["Addresses"];

        if (!isPickUp) {
            steps.push("Shipping");
        }

        if (!requiresApproval) {
            steps.push("Payment");
        }

        steps.push("Review");

        return steps;
    };

    const steps = buildSteps();

    const url = cart?.type === "Quote" ? `${checkoutShippingPage?.url}?cartId=${cartId}` : checkoutShippingPage?.url;

    const goToStep = (e: React.FormEvent<HTMLFormElement>, step: number) => {
        e.preventDefault();
        if (step === 0) {
            // step = 0 will be addresses page
            if (StoreStep !== 0) {
                history.push(url as string);
                dispatch({
                    type: "xCheckoutStepper/update",
                    step,
                });
            }
        } else {
            // step !== 0 means the user is going to other steps other than addresses step
            // eslint-disable-next-line no-lonely-if
            if (StoreStep > step) {
                dispatch({
                    type: "xCheckoutStepper/update",
                    step,
                });
            }
        }
    };

    React.useEffect(() => {
        window.scroll(0, 0);
    }, [StoreStep, isPickUp]);

    return (
        <StyledWrapper {...styles.wrapperStyle}>
            <Hidden
                above="xs"
                css={css`
                    display: flex;
                    justify-content: flex-end;
                    @media (min-width: 481px) {
                        display: none;
                    }
                `}
            >
                <OverflowMenu position="end">
                    {steps.map((step: any, index: any) => (
                        <StyledWrapper
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            css={css`
                                ${styles.stepStyles?.css}
                                padding-top: 0;
                                margin-bottom: 0;
                                border-top: 0 !important;
                                padding: 10px 8px !important;
                            `}
                        >
                            <Clickable
                                onClick={(event: React.FormEvent<any>) => goToStep(event, index)}
                                {...styles.stepClickableStyles}
                                {...({ StoreStep, asLink: true } as any)}
                                disabled={StoreStep < index}
                                className={`${StoreStep === index && "active"} ${StoreStep > index && "completed"} ${
                                    StoreStep < index && "disabled"
                                }`}
                            >
                                <StyledWrapper {...({ StoreStep } as any)} className="step-text">
                                    <Typography
                                        variant="h6"
                                        as="span"
                                        css={css`
                                            letter-spacing: 0.35px;
                                            padding: 0 !important;
                                            margin-bottom: 0;
                                        `}
                                    >
                                        {index + 1}. &nbsp;{step}
                                    </Typography>
                                </StyledWrapper>
                            </Clickable>
                        </StyledWrapper>
                    ))}
                </OverflowMenu>
            </Hidden>
            <Hidden
                below="sm"
                css={css`
                    flex: 3;
                    @media (max-width: 480px) {
                        display: none;
                    }
                `}
            >
                <StyledWrapper {...styles.stepsWrapperStyles}>
                    {steps.map((step: any, index: any) => (
                        <StyledWrapper
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            {...styles.stepStyles}
                            className={`${StoreStep === index && "active"} ${StoreStep > index && "completed"} ${
                                StoreStep < index && "disabled"
                            } ${steps.length === 2 && "stps2"} ${steps.length === 3 && "stps3"} `}
                        >
                            <Clickable
                                onClick={(event: React.FormEvent<any>) => goToStep(event, index)}
                                {...styles.stepClickableStyles}
                                {...({ StoreStep } as any)}
                                disabled={StoreStep < index}
                                className={`${StoreStep === index && "active"} ${StoreStep > index && "completed"} ${
                                    StoreStep < index && "disabled"
                                }`}
                            >
                                <StyledWrapper
                                    {...styles.stepNumberStyles}
                                    {...({ StoreStep } as any)}
                                    className="step-number"
                                >
                                    <span className="number">{index + 1}</span>
                                    <Icon src="Check" className="icon-check" size={18} color="common.white"></Icon>
                                </StyledWrapper>
                                <StyledWrapper {...({ StoreStep } as any)} className="step-text">
                                    <Typography {...styles.stepTextStyles} {...({ StoreStep } as any)}>
                                        {step}
                                    </Typography>
                                </StyledWrapper>
                            </Clickable>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutStepper)),
    definition: {
        group: "XNGAGE" as any,
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Stepper",
    },
};

export default widgetModule;
